import { isAfter } from 'date-fns'

const isValidDate = (d: Date) => Number.isFinite(d.getTime())

/**
 * Localized date + time for display (browser locale, user's local timezone).
 * Does not append timezone name/abbreviation.
 */
export const formatLocaleDateTime = (value?: string | Date | null): string => {
  if (!value) {
    return ''
  }
  const dateObject = new Date(value)
  if (!isValidDate(dateObject)) {
    return ''
  }
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(dateObject)
}

/**
 * Localized weekday + date + time (no timezone label).
 */
export const formatLocaleDateTimeWithWeekday = (
  value?: string | Date | null,
): string => {
  if (!value) {
    return ''
  }
  const dateObject = new Date(value)
  if (!isValidDate(dateObject)) {
    return ''
  }
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(dateObject)
}

/** Short month + day for charts/labels (browser locale). */
export const formatLocaleMonthDay = (value: string | Date): string => {
  const dateObject = new Date(value)
  if (!isValidDate(dateObject)) {
    return ''
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(dateObject)
}

export const formatLocaleDate = (
  value: string | Date,
  options?: { withYear?: boolean; month?: 'short' | 'long' | 'numeric' },
): string => {
  const dateObject = new Date(value)
  if (!isValidDate(dateObject)) {
    return ''
  }

  return new Intl.DateTimeFormat(undefined, {
    month: options?.month ?? 'short',
    day: 'numeric',
    ...(options?.withYear ? { year: 'numeric' } : {}),
  }).format(dateObject)
}

export const dateToTimestamp = (date: string | Date | undefined) => {
  if (!date) {
    return 'Missing Date'
  }
  const dateObject = new Date(date)

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }
  const normalDate = new Intl.DateTimeFormat('en-US', options).format(
    dateObject,
  )

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }
  const normalTime = new Intl.DateTimeFormat('en-US', timeOptions).format(
    dateObject,
  )

  const timestamp = `${normalDate} ${normalTime}`

  return timestamp
}

export const dateToEmailTimestamp = (date: string | Date | undefined) => {
  if (!date) {
    return 'Missing Date'
  }
  const dateObject = new Date(date)

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }
  const normalDate = new Intl.DateTimeFormat('en-US', options).format(
    dateObject,
  )

  return normalDate
}

export const getHourAndMinute = (date: string | Date) => {
  const dateObject = new Date(date)

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }

  const hourAndMinute = new Intl.DateTimeFormat('en-US', timeOptions).format(
    dateObject,
  )

  return hourAndMinute
}

export const getStartDate = (numberOfDays: number) => {
  const today = new Date()
  const startDate = new Date(today)
  startDate.setDate(today.getDate() - numberOfDays + 1)
  const formatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  const [{ value: month }, , { value: day }, , { value: year }] =
    formatter.formatToParts(startDate)

  return `${year}-${month}-${day}`
}

export const getTimezone = () => {
  return (
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
  )
}

/**
 * Formats a date to a short format with month abbreviation and day.
 * Returns a string in the format "Nov 27" (e.g., "Jan 1", "Dec 25").
 *
 * @param date - The date to format (string, Date object, or undefined)
 * @returns Formatted date string in "MMM d" format, or empty string if date is invalid
 */
export const formatShortDate = (date?: string | Date | null) => {
  if (!date) {
    return ''
  }

  const dateObject = new Date(date)

  if (Number.isNaN(dateObject.getTime())) {
    return ''
  }

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  }

  return new Intl.DateTimeFormat('en-US', options).format(dateObject)
}

export const dateToShortMonthFormat = (dateString: string) => {
  const dateObject = new Date(dateString)
  if (!isValidDate(dateObject)) {
    return ''
  }
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(dateObject)
}

export const dateToSlashFormat = (dateString: string) => {
  if (!dateString) {
    return ''
  }
  const dateObject = new Date(dateString)
  if (!isValidDate(dateObject)) {
    return ''
  }
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(dateObject)
}

export const safeDateCompare = (
  a?: string | Date | null,
  b?: string | Date | null,
) => {
  const safeDate = (value?: string | Date | null): Date | null => {
    if (!value) return null
    const d = new Date(value)
    return Number.isFinite(d.getTime()) ? d : null
  }
  const da = safeDate(a)
  const db = safeDate(b)
  if (!da && !db) return 0
  if (!da) return 1
  if (!db) return -1
  if (isAfter(da, db)) return 1
  if (isAfter(db, da)) return -1
  return 0
}
